define([
  'underscore',
  'moment-timezone',
  'es6!sumo/util',
  'es6!lib/sumome/react/util/Font',
  'es6!launcher/util/listbuilder/constants',
  'es6!launcher/util/listbuilder/wysiwyg/constants',
  'es6!launcher/util/listbuilder/wysiwyg/util',
  'es6!launcher/util/listbuilder/wysiwyg/dom',
  'es6!launcher/util/listbuilder/wysiwyg/templates',
], (_, moment, Util, FontUtil, Constants, WysiwygConstants, WysiwygUtil, WysiwygDom, WysiwygTemplates) => {
  // Sanitization functions for wysiwyg json.
  // These functions don't add errors or anything like that.  Instead, they check
  // component values such as style, placeholders, etc. and make sure they are valid.
  // If they aren't, a default is put in place so we can still render the components.
  const COLOR_REGEX = /^rgba\([0-9]+,[0-9]+,[0-9]+,([01]|[01]?\.[0-9]*)\)$/;
  const BLACK = 'rgba(0,0,0,1)';
  const WHITE = 'rgba(255,255,255,1)';
  const TRANSPARENT = 'rgba(0,0,0,0)';

  // This is more of a temporary fix to transfer the popup styles to a background component.
  // Once all templates and popups are saved with background components, this can be removed.
  function sanitizePopupStyle(popupJson) {
    const style = popupJson.style;

    // If no style, then it's already been converted.
    if (!style) return;

    const backgroundTemplate = WysiwygTemplates.getTemplate('background');
    backgroundTemplate.style = style;

    // Ensure components is an array.
    popupJson.components = popupJson.components || [];

    popupJson.components.unshift(backgroundTemplate);

    // Remove popupJson style since we are going to a background component.  This will
    // be removed when all templates are updated.
    delete popupJson.style;
  }

  function sanitizePositioning(respectPercentages, components) {
    let minTop;
    let minLeft;

    // Give position if component doesn't have one, and figure out what
    // the minimum top and left component is so we can normalize the positions.
    _.each(components, component => {
      const style = component.style || {};

      // Don't need margins or padding since we are absolutely positioning.
      style.padding = '0';
      style.margin = '0';

      if (!style.top) style.top = '0px';
      if (!style.left) style.left = '0px';

      component.style = style;

      // If the component is hidden, we don't want to include in the calculation.
      if (component.type === 'hidden') return;

      // Get the smallest top and left position
      const top = WysiwygUtil.getPixelValue(style.top) || 0;
      const left = WysiwygUtil.getPixelValue(style.left) || 0;

      // First component but also need to make sure the value isn't zero
      // since that is falsy as well.
      if (!minTop && minTop !== 0) minTop = top;
      if (!minLeft && minLeft !== 0) minLeft = left;

      minTop = Math.min(minTop, top);
      minLeft = Math.min(minLeft, left);
    });

    // if we're working with rows, we're responsive, so don't reposition
    if (components.length > 0 && components[0].type === 'gridRow') return;

    // If top and left are zero, then we don't need to move anything.
    if (!minTop && !minLeft) return;

    _.each(components, component => {
      const style = component.style || {};

      const top = WysiwygUtil.getPixelValue(style.top) || 0;
      const left = WysiwygUtil.getPixelValue(style.left) || 0;

      style.top = `${top - minTop}px`;
      style.left = `${left - minLeft}px`;

      component.style = style;
    });
  }

  function sanitizeSize(respectPercentages, components) {
    _.each(components, (component) => {
      const style = component.style || {};
      const isPercentWidth = respectPercentages && style.width && style.width.indexOf('%') !== -1;
      const isPercentHeight = respectPercentages && style.height && style.height.indexOf('%') !== -1;

      const width = isPercentWidth ? style.width : WysiwygUtil.getPixelValue(style.width);
      const height = isPercentHeight ? style.height : WysiwygUtil.getPixelValue(style.height);
      const type = component.type;

      // Force a minimum size on components, except on grid components
      if (type !== 'gridRow' && type !== 'gridColumn') {
        if ((isNaN(width) || width < 1) && !isPercentWidth) style.width = '100px';
        if ((isNaN(height) || height < 1) && !isPercentHeight) style.height = '100px';

        component.style = style;
      }
    });
  }

  function sanitizeTextAlign(components) {
    const VALID_TEXT_ALIGN_VALUES = ['left', 'center', 'right'];

    _.each(components, component => {
      const style = component.style || {};
      if (WysiwygConstants.TEXT_ALIGN_COMPONENTS.indexOf(component.type) < 0) return;

      if (VALID_TEXT_ALIGN_VALUES.indexOf(style.textAlign) < 0) {
        style.textAlign = 'left';
      }

      component.style = style;
    });
  }

  function sanitizeFonts(components) {
    _.each(components, (component) => {
      const style = component.style || {};
      if (WysiwygConstants.FONT_COMPONENTS.indexOf(component.type) < 0) return;

      // Make sure font styles aren't null.
      if (!style.fontFamily || !style.fontFamily.length) {
        style.fontFamily = '\'Open Sans\',sans-serif';
      }
      if (!style.fontWeight || !style.fontWeight.length) style.fontWeight = '400';
      if (!style.fontStyle || !style.fontStyle.length) style.fontStyle = 'normal';
      if (!style.fontSize || !style.fontSize.length) style.fontSize = '16px';

      // Make sure font family is valid for our font options.
      const { face: fontFace } = FontUtil.parseFontFamily(style.fontFamily);
      let fontOption = FontUtil.getFontOption(fontFace);

      // Default to Open Sans if our font face isn't an option.
      if (!fontOption) fontOption = FontUtil.getFontOption('Open Sans');

      if (FontUtil.isBrowserFont(fontOption.face)) {
        // We just save the font face for browser defaults.  When rendering, we can
        // check if it is a browser font and render the correct font family.
        style.fontFamily = fontOption.face;
      } else {
        style.fontFamily = `'${fontOption.face}',${fontOption.generic}`;
      }

      if (!style.lineHeight || !style.lineHeight.length) style.lineHeight = '1.5';

      component.style = style;
    });
  }

  function sanitizeColors(components) {
    _.each(components, component => {
      const style = component.style || {};

      if (WysiwygConstants.BACKGROUND_COLOR_COMPONENTS.indexOf(component.type) >= 0) {
        if (!style.backgroundColor || !style.backgroundColor.length) style.backgroundColor = TRANSPARENT;
        // Make sure color matches the color format.
        if (!COLOR_REGEX.test(style.backgroundColor)) style.backgroundColor = TRANSPARENT;

        // Blank implies that they have solid color selected.
        if (!style.gradient) style.gradient = '';
      }

      if (WysiwygConstants.COLOR_COMPONENTS.indexOf(component.type) >= 0) {
        if (!style.color || !style.color.length) style.color = BLACK;
        // Make sure color matches the color format.
        if (!COLOR_REGEX.test(style.color)) style.color = BLACK;
      }

      if (WysiwygConstants.BORDER_COMPONENTS.indexOf(component.type) >= 0) {
        if (!style.borderColor || !style.borderColor.length) style.borderColor = BLACK;
        // Make sure color matches the color format.
        if (!COLOR_REGEX.test(style.borderColor)) style.borderColor = BLACK;
      }

      // Make sure there is a default fill color for close buttons.
      if (WysiwygConstants.MEDIA_COMPONENTS.indexOf(component.type) >= 0) {
        if (!style.fill || !style.fill.length) style.fill = BLACK;
      }

      component.style = style;
    });
  }

  function sanitizeBorder(components) {
    _.each(components, component => {
      const style = component.style || {};

      // Border color is sanitized in sanitizeColors().
      if (WysiwygConstants.BORDER_COMPONENTS.indexOf(component.type) >= 0) {
        if (style.borderStyle !== 'solid') style.borderStyle = 'solid';
        if (isNaN(WysiwygUtil.getPixelValue(style.borderWidth))) style.borderWidth = '0px';
        if (isNaN(WysiwygUtil.getPixelValue(style.borderRadius))) {
          style.borderRadius = '0px';
          style.borderTopLeftRadius = '0px';
          style.borderTopRightRadius = '0px';
          style.borderBottomLeftRadius = '0px';
          style.borderBottomRightRadius = '0px';
        }
      }

      component.style = style;
    });
  }

  function sanitizeShadows(components) {
    _.each(components, component => {
      const style = component.style || {};

      if (WysiwygConstants.BOX_SHADOW_COMPONENTS.indexOf(component.type) >= 0) {
        if (!style.boxShadow || !style.boxShadow.length) style.boxShadow = '0px 0px 0px rgba(0,0,0,0), inset 0px 0px 0px rgba(0,0,0,0)';
      }

      if (WysiwygConstants.FONT_COMPONENTS.indexOf(component.type) >= 0) {
        if (!style.textShadow || !style.textShadow.length) style.textShadow = '0px 0px 0px rgba(0,0,0,0)';
      }

      component.style = style;
    });
  }

  function sanitizeMedia(components) {
    _.each(components, component => {
      if (WysiwygConstants.MEDIA_COMPONENTS.indexOf(component.type) < 0) return;

      // If the media component doesn't have a mediaType then give it image.
      if (!component.mediaType) component.mediaType = 'image';
    });
  }

  // Buttons and images need actions.
  function sanitizeComponentActions(components) {
    _.each(components, component => {
      if (WysiwygConstants.ACTION_COMPONENTS.indexOf(component.type) < 0) return;
      let defaultAction = 'submit';
      if (component.type === 'image') defaultAction = 'nothing';

      const action = component.action;
      const isInvalidAction = WysiwygConstants.COMPONENT_ACTIONS.indexOf(action) < 0;
      if (isInvalidAction) component.action = defaultAction;
      // We want a to attr for redirect actions.
      if (!component.to) component.to = '';
      if (!component.redirectMethod) component.redirectMethod = '';
      if (!component.redirectWindow) component.redirectWindow = 'current';
      if (!component.redirectVariableName && component.redirectVariableName !== '') {
        component.redirectVariableName = 'email';
      }
    });
  }

  function sanitizeFields(components) {
    let hasSubmitButton = false;
    let hasEmailField = false;

    _.each(components, component => {
      if (component.action === 'submit') hasSubmitButton = true;
      if (component.name === 'email') hasEmailField = true;

      if (WysiwygConstants.FIELD_COMPONENTS.indexOf(component.type) < 0) return;

      if (!component.required) component.required = false;
      if (!component.name) component.name = '';
      // Value is for fields like hidden and checkbox.
      if (!component.value) component.value = '';

      if (WysiwygConstants.OPTION_COMPONENTS.indexOf(component.type) >= 0) {
        if (!component.options || !component.options.length) {
          component.options = [{ label: 'Option 1', value: '' }];
        }
      }
    });

    // Email required for all forms
    return hasSubmitButton && !hasEmailField;
  }

  function sanitizeFillMode(components) {
    _.each(components, component => {
      if (WysiwygConstants.FILL_COMPONENTS.indexOf(component.type) < 0) return;

      if(WysiwygConstants.COMPONENT_FILL_MODES.indexOf(component.fill) < 0) {
        component.fill = 'normal';
      }
    });
  }

  function sanitizeCountdown(components) {
    const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + WEEK_IN_MS);

    _.each(components, component => {
      if (component.type !== 'countdown') return;

      if (!component.end) component.end = weekFromNow.toString();
      // Default to central timezone because why not.
      if (!component.timezone) component.timezone = moment.tz.guess();
      if (!component.format) component.format = 'UU NN, hh:mm:ss';
      if (!component.styleName) component.styleName = 'classic';
      if (!component.timerType) component.timerType = 'fixed';
      if (!component.whenTimerEnds) component.whenTimerEnds = 'stop';
      if (!component.whenTimerEndsAction) component.whenTimerEndsAction = '';
      if (!component.timerDuration) component.timerDuration = "2";
      if (!component.timerDurationOption) component.timerDurationOption = "hours";
    });
  }

  // Accepts the parsed json column on a popup and loops through the popups
  // sanitizing each one.
  function sanitizePopups(formType, parsedJsonForPopups) {
    const respectPercentages = formType === Constants.FormTypes.WELCOME_MAT;
    _.each(_.keys(parsedJsonForPopups), (key) => {
      const popupJson = parsedJsonForPopups[key] || {};

      sanitizePopupStyle(popupJson);

      // Sanitize popup before getting components.
      const components = WysiwygDom.getDescendantComponentsJSON(popupJson.components || []);

      // Add ids to components so we can track them in wysiwyg.
      WysiwygDom.addIdsToComponents(components);

      // Sanitize styles on components.
      sanitizePositioning(respectPercentages, components);
      sanitizeSize(respectPercentages, components);
      sanitizeTextAlign(components);
      sanitizeFonts(components);
      sanitizeColors(components);
      sanitizeBorder(components);
      sanitizeShadows(components);

      sanitizeMedia(components);
      sanitizeComponentActions(components);
      sanitizeFields(components);
      sanitizeFillMode(components);
      sanitizeCountdown(components);

      parsedJsonForPopups[key] = popupJson;
    });
  }

  function cleanPopups(parsedJsonForPopups) {
    _.each(_.keys(parsedJsonForPopups), key => {
      const popupJson = parsedJsonForPopups[key] || {};
      const components = popupJson.components || [];

      // Remove component ids since they don't need to be saved.
      WysiwygDom.removeIdsFromComponents(components);
      // Remove component src since they don't need to be saved.
      // This is more temporary since we used to have src instead of mediaId
      // for images.
      WysiwygDom.removeSrcFromComponents(components);
      // cleanPopups is only called when saving and save is only called
      // if there aren't errors so we can safely assume that any error
      // objects are blank and we are good to go.
      popupJson.errors = undefined;
      WysiwygDom.removeErrorsFromComponents(components);

      popupJson.components = components;
      parsedJsonForPopups[key] = popupJson;
    });
  }

  return {
    sanitizePopups,
    cleanPopups,
  };
});
